import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react'
import { PremiumFeature } from '../proto/generated/subscription'
import { useSelector } from 'react-redux'
import { selectUserSubscriptionPlans, selectAllFeatures, selectUserSubscriptionPlansData } from '../selectors/user'
import { hasFeatureAccess } from '../util/accessControl'
import { FeatureCategory, FeatureName } from '../components/adminUserRestrictions/Restrictions.constants'

interface User {
    subscriptionPlans: string[]
    premiumFeatures: PremiumFeature[]
    simulatorAccessible: boolean
    multiStrikeChartAccessible: boolean
    scalperAccessible: boolean
    hasAccess: (category: string, subCategory?: string, feature?: string) => boolean
}

interface UserContextType {
    user: User
    setUser: React.Dispatch<React.SetStateAction<User>>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const userSubscriptionPlans = useSelector(selectUserSubscriptionPlans)
    const userSubscriptionPlansData = useSelector(selectUserSubscriptionPlansData)
    const allFeatures = useSelector(selectAllFeatures)

    const userFeatures = useMemo(()=>{
        if (!userSubscriptionPlansData || !userSubscriptionPlans || userSubscriptionPlans.length === 0) {
            return undefined
        }
        const allFeatures: PremiumFeature[] = []
        userSubscriptionPlans.forEach((plan) => {
            const features = userSubscriptionPlansData[plan]
            if (features) {
                allFeatures.push(...features)
            }
        })

        return allFeatures
    },[userSubscriptionPlansData, userSubscriptionPlans])


    const hasAccess = useCallback((category: string, subCategory?: string, feature?: string) => {
        return hasFeatureAccess(allFeatures, userFeatures, category, subCategory, feature)
    }, [allFeatures, userFeatures])

    const [user, setUser] = useState<User>({
        subscriptionPlans: userSubscriptionPlans ?? [],
        premiumFeatures: allFeatures,
        simulatorAccessible: false,
        multiStrikeChartAccessible: false,
        scalperAccessible: false,
        hasAccess
    })

    useEffect(() => {
        const simulatorAccessible = hasFeatureAccess(allFeatures, userFeatures, FeatureCategory.SIMULATOR, '', FeatureName.SIMULATOR_ACCESS)
        const multiStrikeChartAccessible = hasFeatureAccess(allFeatures, userFeatures, FeatureCategory.MULTI_STRIKE, '', FeatureName.MULTI_STRIKE_ACCESS)
        const scalperAccessible = hasFeatureAccess(allFeatures, userFeatures, FeatureCategory.SCALPER, '', FeatureName.SCALPER_ACCESS)
        const newUser: User = {
            subscriptionPlans: userSubscriptionPlans ?? [],
            premiumFeatures: allFeatures,
            simulatorAccessible,
            multiStrikeChartAccessible,
            scalperAccessible,
            hasAccess: hasAccess
        }
        setUser(newUser)
    }, [userSubscriptionPlans, userSubscriptionPlansData, allFeatures, hasAccess])

    return <UserContext.Provider value={{ user, setUser }}>{children}</UserContext.Provider>
}

export const useUser = (): UserContextType => {
    const context = useContext(UserContext)
    if (!context) throw new Error('useUser must be used within a UserProvider')
    return context
}

/** Safe version for components that may render outside UserProvider (e.g. in layout). Returns null when outside provider. */
export const useUserOptional = (): UserContextType | null => useContext(UserContext)
